import { Box, LinearProgress, Stack, Typography } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useCreateVisitMutation } from "src/api/services/visitApi";
import {
  OneTimeProduct,
  ProductCategories,
  ProductsWithMedicationForm,
} from "src/api/types";
import { Paths } from "src/constants/paths";
import { useIsMobile } from "src/hooks/useIsMobile";
import AuthQueue from "src/pages/transitions/AuthQueue";
import WhiteCard from "src/shared/components/WhiteCard";
import { MedicationIcon } from "src/shared/components/MedicationIcon";
import ReactivationModal from "src/shared/components/ReactivationModal";
import { usePatientAge } from "src/hooks/usePatientAge";
import { useLatestOnlineVisit } from "src/context/LatestOnlineVisits";
import { differenceInYears } from "date-fns";
import { useGetLatestOrderQuery } from "src/api/services/ordersApi";
import { getQueryParamValue } from "../../utils/getQueryParamValue";
import { createQueryParams } from "src/utils/createQueryParams";
import { usePatientSubscriptions } from "src/context/PatientSubscriptionsProvider";
import { skipToken } from "@reduxjs/toolkit/query";

interface Props {
  product: ProductsWithMedicationForm;
  recommendedDose?: OneTimeProduct;
}

const subtextStyleMap: Record<string, { color: string; weight: number }> = {
  "st-ot": { color: "#00B5B8", weight: 600 },
  "os-ot": { color: "#00B5B8", weight: 600 },
  "tr-ot": { color: "#00B5B8", weight: 600 },
  "bc-ot": { color: "#00B5B8", weight: 600 },
  ec: { color: "#00B5B8", weight: 600 },
  "ed-ot": { color: "#00B5B8", weight: 600 },
  "amh-ot": { color: "#00B5B8", weight: 600 },
  "b-ot": { color: "#00B5B8", weight: 600 },
  "sl-ot": { color: "#00B5B8", weight: 600 },
  "bb-ot": { color: "#00B5B8", weight: 600 },
  "iwl-ot": { color: "#00B5B8", weight: 600 },
  "wp-ot": { color: "#00B5B8", weight: 600 },
};

const Item = ({ product, recommendedDose }: Props) => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const component = "membersFavorite";
  const [createVisit] = useCreateVisitMutation();
  const latestVisit = useLatestOnlineVisit((visits) =>
    visits.find((v) => v.product_id === product.id),
  );
  const canceledSubscription = usePatientSubscriptions((subscriptions) =>
    subscriptions.find(
      (s) => s.status === "canceled" && s.product.id === product.id,
    ),
  );
  const { data: latestOrder } = useGetLatestOrderQuery(
    product.slug === "ed-ot"
      ? {
          productId: product.id,
        }
      : skipToken,
  );
  const [searchParams] = useSearchParams();
  const [visitQueue, setVisitQueue] = useState<boolean>(false);
  const [queryParam, setQueryParam] = useState<string>("");
  const [openReactivation, setOpenReactivation] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const age = usePatientAge();
  const height = useMemo(() => (isMobile ? 250 : 320), [isMobile]);

  const name = useMemo(() => {
    if (product.slug === "on") {
      return t(`membersFavorite.${product.slug}.name`);
    }

    if (product.slug === "bc" && age > 55) {
      return t(`membersFavorite.mp.name`, "Menopause Care");
    }

    return t(`medications.${product.display_name.toLowerCase()}.capitalized`);
  }, [age, product.display_name, product.slug, t]);

  const subtext = useMemo(() => {
    if (product.slug === "mt") return null;

    if (["Hair Loss", "Erectile Dysfunction"].includes(product.category.name)) {
      return t(
        `${component}.${product.display_name.toLowerCase()}.${
          product.gender
        }.subtext`,
      );
    }

    return t(`${component}.${product.slug}.subtext`);
  }, [product.category, product.display_name, product.gender, product.slug, t]);

  const caption = useMemo(() => {
    if (["Hair Loss", "Erectile Dysfunction"].includes(product.category.name)) {
      return t(
        `${component}.${product.display_name.toLowerCase()}.${
          product.gender
        }.caption`,
      );
    }

    return t(`${component}.${product.slug}.caption`);
  }, [product.category, product.display_name, product.gender, product.slug, t]);

  const paramValue = useMemo(() => {
    if (product.slug === "ed-ot") {
      const medication = latestOrder?.details.medications[0].display_name || "";
      const value = getQueryParamValue(medication, product.display_name);

      return createQueryParams({ variant: value });
    }

    if (
      product.category.name === ProductCategories.HairLoss &&
      product.form != null
    ) {
      return createQueryParams({ form: product.form });
    }

    if (
      recommendedDose != null &&
      product.slug === recommendedDose.product.slug
    ) {
      const maxDose = recommendedDose.dosage.split(" - ")[1];

      return createQueryParams({
        dosage: maxDose,
        id: recommendedDose.price.stripe_id,
        amount: String(recommendedDose.price.amount),
      });
    }

    return "";
  }, [
    latestOrder?.details.medications,
    product.category.name,
    product.display_name,
    product.form,
    product.slug,
    recommendedDose,
  ]);

  const visitCompletedOver1Year = useMemo(() => {
    if (latestVisit?.completed_at == null) return true;

    const completionTime = differenceInYears(
      new Date(),
      new Date(latestVisit.completed_at || ""),
    );

    return completionTime >= 1;
  }, [latestVisit]);

  const handleClick = useCallback(async () => {
    setLoading(true);
    let qs: string = "";

    if (canceledSubscription != null) {
      setOpenReactivation(true);
      setLoading(false);
      return;
    }

    if (!visitCompletedOver1Year) {
      switch (product.slug) {
        case "ed-ot": {
          const params = new URLSearchParams(paramValue);
          qs = `?${params.toString()}`;

          navigate(`/${Paths.ADD_ONS}/${product.slug}${qs}`);
          return;
        }
        case "fhl-ot":
        case "fhl":
        case "mhl-ot": {
          const params = new URLSearchParams(paramValue);
          qs = `?${params.toString()}`;

          navigate(`/${Paths.ADD_ONS}/${product.slug}${qs}`);
          return;
        }
        case "st-ot":
        case "tr-ot": {
          const params = new URLSearchParams(paramValue);
          qs = `?${params.toString()}`;

          navigate(`/${Paths.ADD_ONS}/${product.slug}${qs}`);
          return;
        }
        default: {
          navigate(`/${Paths.ADD_ONS}/${product.slug}`);
          return;
        }
      }
    }

    if (
      product.form != null &&
      product.category.name === ProductCategories.HairLoss
    ) {
      const params = new URLSearchParams(searchParams);
      params.set("form", product.form);
      qs = `?${params.toString()}`;
    }

    if (latestVisit != null && latestVisit.completed_at == null) {
      setVisitQueue(true);
      setQueryParam(qs);
      return;
    }

    const { data } = await createVisit({
      product: product.slug,
      visit_type: "Upsell",
    });
    if (data != null) {
      navigate(
        `/${product.slug}/${data.slug}/${Paths.ADD_ONS}/${data.intake.first_question}${qs}`,
      );
    }
  }, [
    canceledSubscription,
    createVisit,
    latestVisit,
    navigate,
    paramValue,
    product.category.name,
    product.form,
    product.slug,
    searchParams,
    visitCompletedOver1Year,
  ]);

  return (
    <>
      <WhiteCard
        height={height}
        sx={{ cursor: "pointer" }}
        width={isMobile ? 190 : 240}
      >
        <Box>
          {loading ? (
            <LinearProgress color="secondary" sx={{ height: 4 }} />
          ) : null}
          {visitQueue ? (
            <AuthQueue
              visitType={"Upsell"}
              productId={product.id}
              queryParams={queryParam}
            />
          ) : null}
          <Stack
            gap={isMobile ? 0 : 1}
            onClick={!loading || !visitQueue ? handleClick : undefined}
            sx={{ opacity: loading ? 0.5 : 1 }}
          >
            <Stack sx={{ height: isMobile ? 70 : 50 }}>
              <Typography
                fontWeight={700}
                variant={isMobile ? "body2" : "body1"}
              >
                {name}
              </Typography>
              {subtext ? (
                <Typography
                  variant={isMobile ? "caption" : "body2"}
                  color={subtextStyleMap[product.slug]?.color || "#000000"}
                  fontWeight={subtextStyleMap[product.slug]?.weight || 400}
                >
                  {subtext}
                </Typography>
              ) : (
                <br />
              )}
            </Stack>
            <Stack
              alignItems="center"
              justifyContent="center"
              sx={{
                position: "relative",
              }}
            >
              <MedicationIcon
                name={product.display_name}
                width={isMobile ? 150 : 230}
                height={isMobile ? 135 : 220}
                fit="contain"
                slug={product.slug}
              />
            </Stack>
            <Typography
              variant={isMobile ? "caption" : "subtitle2"}
              fontStyle={"italic"}
              sx={{ color: "#6C7278" }}
            >
              {caption}
            </Typography>
          </Stack>
        </Box>
      </WhiteCard>
      <ReactivationModal
        subscriptionId={canceledSubscription?.id}
        open={openReactivation}
        onClose={setOpenReactivation}
      />
    </>
  );
};

export default Item;
